import React, { useState } from 'react';
import { useAdminPermissions } from '../../hooks/useAdminPermissions.js';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import GlobalElevenLabsSettings from '../../components/admin/GlobalElevenLabsSettings.jsx';

/**
 * AdminSettings page containing provider settings and configuration
 */
const AdminSettings = () => {
  const { isAdmin } = useAdminPermissions();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('elevenlabs');

  if (!isAdmin) {
    return (
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="text-center">
          <h2 className={`text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Access Denied</h2>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>You don't have permission to access provider settings.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'twilio',
      name: 'Twilio',
      icon: '📞',
      disabled: true
    },
    {
      id: 'elevenlabs',
      name: 'ElevenLabs',
      icon: '🎤',
      disabled: false
    },
    {
      id: 'openai',
      name: 'OpenAI',
      icon: '🤖',
      disabled: true
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'twilio':
        return (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="text-center">
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Twilio Settings</h3>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Twilio provider settings will be available soon.</p>
            </div>
          </div>
        );
      case 'elevenlabs':
        return <GlobalElevenLabsSettings />;
      case 'openai':
        return (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="text-center">
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>OpenAI Settings</h3>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>OpenAI provider settings will be available soon.</p>
            </div>
          </div>
        );
      default:
        return <GlobalElevenLabsSettings />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Provider Settings</h1>
        <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Manage global provider configurations and settings
        </p>
      </div>

      {/* Tabs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className={`border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors
                  ${tab.disabled
                    ? `cursor-not-allowed opacity-50 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} border-transparent`
                    : activeTab === tab.id
                      ? `border-teal-500 ${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'}`
                      : `border-transparent ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                  }
                `}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
                {tab.disabled && (
                  <span className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                    Soon
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
